import { BrowserRouter } from "react-router-dom";
import { Logo } from "@/common/Logo/Logo.tsx";
import { useLightSurface } from "@/app/darkMode.ts";
import { AuthHeading, BrandButton } from "../auth/components/BrandControls.tsx";
import { AuditProfileCard } from "./AuditProfilePanel.tsx";
import {
    AuditProspectProvider,
    useAuditProspect,
} from "./AuditProspectContext.tsx";

/**
 * An audit link opened by somebody who is signed in already: one of us
 * checking a link before it is sent, a customer who was forwarded one, or
 * the prospect coming back after signing up.
 *
 * It is a preview. The landing screen is a sign-up form, which they have no
 * use for, so they get the profile on its own and the way back into their
 * account. Nothing here is recorded as the prospect's doing: the read does
 * not count as an open, and the profile cannot be edited. Using the profile
 * in a workspace of theirs is a later step
 * (specs/icp-fit/outbound-audit.md §3).
 */
function SignedInLanding() {
    useLightSurface();
    const { token, state } = useAuditProspect();

    return (
        <div className="min-h-dvh bg-white font-sans text-brand-purple">
            <div className="mx-auto flex w-full max-w-xl flex-col gap-8 px-6 py-10">
                <Logo
                    variant="color+primary"
                    className="h-6 w-auto self-start"
                />
                {state.status === "loading" && (
                    <p className="text-sm text-brand-grey-warm">One moment…</p>
                )}
                {state.status === "closed" && (
                    // A link closes when its prospect signs up, and whoever
                    // reads this is signed in: most likely that is why.
                    <AuthHeading
                        title="This link is no longer open"
                        subtitle="If you signed up from it, you are all set. Carry on in Spectacle."
                    />
                )}
                {state.status === "failed" && (
                    <AuthHeading
                        title="We could not open this page"
                        subtitle={state.message}
                    />
                )}
                {state.status === "ready" && token !== null && (
                    <>
                        <AuthHeading
                            title="Is this who you sell to?"
                            subtitle={`A preview of the page made for ${state.prospect.domain}. You are signed in, so the sign-up form and the editor are left out, and this visit is not counted.`}
                        />
                        <AuditProfileCard
                            token={token}
                            prospect={state.prospect}
                            editable={false}
                        />
                    </>
                )}
                {/* A full load: the app's own router is not mounted here. */}
                <BrandButton
                    variant="outline"
                    onClick={() => window.location.assign("/")}
                >
                    Go to Spectacle
                </BrandButton>
            </div>
        </div>
    );
}

export default function AuditSignedInFlow() {
    return (
        <BrowserRouter>
            <AuditProspectProvider preview>
                <SignedInLanding />
            </AuditProspectProvider>
        </BrowserRouter>
    );
}
